import { remove } from 'lodash';
import { animateScrollPromise } from '../../../utils/animation/animateScrollIntoView';
import { toCssPx } from '../../../utils/cssUtil';

import { ScrollToElementConfig } from './scrollToElementUtils';
import { SheetId } from '../../structural/sheet/sheetTypes';

export type ScrollCleanupConfig = { scrollableParent?: HTMLElement | null | undefined; sheetId?: SheetId | undefined };

type ScrollExcessState = { scrollableParent: HTMLElement; sheetId: SheetId | undefined; paddingBottomPx: number };

const scrollExcessStateList: Array<ScrollExcessState> = [];

/**
 * This function is used to clean up scroll excess padding added before for a specific scrollableParent or sheetId.
 */
export const cleanupScrollExcessPadding = async ({ scrollableParent, sheetId }: ScrollCleanupConfig) => {
    const scrollExcessToClean = remove(scrollExcessStateList, (entry) => {
        if (scrollableParent) return entry.scrollableParent === scrollableParent;
        if (sheetId) return entry.sheetId === sheetId;
    });

    if (!scrollExcessToClean) return;

    scrollExcessToClean.forEach(({ scrollableParent, paddingBottomPx }) => {
        if (!scrollableParent || !scrollableParent.parentElement) return;

        const currentScrollLeft = scrollableParent.scrollLeft;
        const currentScrollTop = scrollableParent.scrollTop;

        // A scrollableParent might have multiple scroll excess paddings applied to it.
        // So when cleaning up one of it's scroll excess paddings, we want to ensure that
        // the remaining scroll excess paddings are still applied correctly.
        const newPaddingBottomPx = scrollExcessStateList.reduce((acc, entry) => {
            if (entry.scrollableParent !== scrollableParent) return acc;
            return Math.max(acc, entry.paddingBottomPx);
        }, 0);

        const paddingBottomPxDiff = paddingBottomPx - newPaddingBottomPx;
        const maxScrollTop = Math.max(
            scrollableParent.scrollHeight - scrollableParent.clientHeight - paddingBottomPxDiff,
            0,
        );

        if (currentScrollTop <= maxScrollTop) {
            scrollableParent.style.paddingBottom = toCssPx(newPaddingBottomPx);
            return;
        }

        animateScrollPromise({
            scrollableParent,
            interpolationFactor: 0.2,
            fromScrollLeft: currentScrollLeft,
            fromScrollTop: currentScrollTop,

            // Scroll to the bottom of the page (without scrollExcess)
            toScrollLeft: currentScrollLeft,
            toScrollTop: maxScrollTop,
        }).then(() => {
            scrollableParent.style.paddingBottom = toCssPx(newPaddingBottomPx);
        });
    });
};

/**
 * When animating to element at the bottom of the screen, there are situations where toScrollTop is
 * not accessible as it exceeds the possible scrollable area.
 *
 * In this case, we want to add in extra margin to the bottom of scrollableElement to allow for the scroll
 */
export const applyScrollExcessPadding = (
    scrollConfig: ScrollToElementConfig | undefined,
    sheetId: SheetId | undefined,
): Promise<void> =>
    new Promise((resolve) => {
        const { scrollableParent, bottomObstructionHeight = 0 } = scrollConfig || {};

        if (!scrollableParent || bottomObstructionHeight === 0) return resolve();

        const scrollableParentRect = scrollableParent.getBoundingClientRect();
        const paddingBottomPx = bottomObstructionHeight - (window.innerHeight - scrollableParentRect.bottom);

        // Set animateScrollToResetScrollExcess function to animate back to position that resets the scrollExcess back to 0
        // - This function will be called on finish editing an element
        scrollExcessStateList.push({
            scrollableParent,
            sheetId,
            paddingBottomPx,
        });

        requestAnimationFrame(() => {
            scrollableParent.style.paddingBottom = toCssPx(paddingBottomPx);
            resolve();
        });
    });
